import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row, FormInstance } from 'antd';
import { Select } from 'lib/components/Select/Select';
import { Input } from 'lib/components/Input/Input';
import { UpdateCustomerInput } from 'app/cross-cutting-concerns/communication/interfaces/am-sp-api-graphql';
import { CustomerListActions } from 'app/modules/customer-management/customer-list/state/customerListSlice';
import { customerListSelector } from 'app/modules/customer-management/customer-list/state/customerListSelectors';
import { CustomerDetailsSelectors } from 'app/modules/customer-management/customer-details/state/customerDetailsSelectors';

interface CreateCustomerFormProps {
  formInstance: FormInstance<UpdateCustomerInput>;
  onFinish: (values: UpdateCustomerInput) => void;
}

const { Option } = Select;

export const CustomerEditForm = ({ formInstance, onFinish }: CreateCustomerFormProps): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const algs = useSelector(customerListSelector.selectAlgsFilters);

  const isListAlgsLoading = useSelector(customerListSelector.selectIsFiltersLoading);

  const customerDetails = useSelector(CustomerDetailsSelectors.selectData);

  useEffect(() => {
    dispatch(CustomerListActions.customerListAlgsRequest());
  }, [dispatch]);

  return (
    <Form
      form={formInstance}
      name="customer-update"
      layout="vertical"
      onFinish={onFinish}
      autoComplete="off"
      initialValues={{ ...customerDetails }}
    >
      <Row gutter={24}>
        <Col xs={24} sm={12}>
          <Form.Item
            name="customerName"
            label={t('customerCreate.form.customerName')}
            className="customer-update__input-group"
            required
            rules={[{ required: true, message: t('customerCreate.form.errors.customerNameRequired') }]}
          >
            <Input className="customer-update__input" placeholder={t('customerCreate.form.placeholder.common')} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="alg"
            label={t('customerCreate.form.alg')}
            className="customer-update__input-group"
            required
            rules={[{ required: true, message: t('customerCreate.form.errors.algRequired') }]}
          >
            <Select
              className="customer-update__select"
              placeholder={t('customerCreate.form.placeholder.alg')}
              loading={isListAlgsLoading}
              showArrow
              showSearch
            >
              {algs?.map(alg => (
                <Option value={alg} key={alg}>
                  {alg}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={24}>
        <Col xs={24} sm={12}>
          <Form.Item
            name="sapNumber"
            label={t('customerCreate.form.sapNumber')}
            className="customer-update__input-group"
          >
            <Input className="customer-update__input" placeholder={t('customerCreate.form.placeholder.common')} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={24}>
        <Col xs={24} sm={12}>
          <Form.Item name="street" label={t('customerCreate.form.street')} className="customer-update__input-group">
            <Input className="customer-update__input" placeholder={t('customerCreate.form.placeholder.street')} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="houseNumber"
            label={t('customerCreate.form.houseNumber')}
            className="customer-update__input-group"
          >
            <Input className="customer-update__input" placeholder={t('customerCreate.form.placeholder.houseNumber')} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={24}>
        <Col xs={24} sm={12}>
          <Form.Item name="zipCode" label={t('customerCreate.form.zipCode')} className="customer-update__input-group">
            <Input className="customer-update__input" placeholder={t('customerCreate.form.placeholder.zipCode')} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item name="city" label={t('customerCreate.form.city')} className="customer-update__input-group">
            <Input className="customer-update__input" placeholder={t('customerCreate.form.placeholder.city')} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={24}>
        <Col xs={24} sm={12}>
          <Form.Item name="region" label={t('customerCreate.form.region')} className="customer-update__input-group">
            <Input className="customer-update__input" placeholder={t('customerCreate.form.placeholder.region')} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item name="district" label={t('customerCreate.form.district')} className="customer-update__input-group">
            <Input className="customer-update__input" placeholder={t('customerCreate.form.placeholder.district')} />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};
